import { Injectable } from '@angular/core';
import { STATUS_POD_CODE } from '../constants/status-pod';
import { IShipmentLocation } from '@cemex-core/cmx-api';
import { Dictionary } from 'lodash';
import { UntypedFormGroup } from '@angular/forms';
import { FieldSelectConfig } from '../modules/configurable-form/components/form-select/models/field-select-config.interface';
import { AddressService } from './address.service';
import { ORDER_EXPERIENCES } from '../constants/order-experiences';
import { PodOptionsBuilderConfig } from 'app/shared/global-filters/models/pod-options-builder-config.interface';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1/';
import { Features } from '@ordersnproducts/features';
import { getOptionWithAddress } from '@ordersnproducts/modules/shared/utils/location-dropdown-option-util';

@Injectable()
export class PodDropdownService {
  /**
   * Constructor for PodDropdownService.
   * @param addressService Service to handle address-related operations.
   */
  constructor(
    private addressService: AddressService,
    private featureToggleService: FeatureToggleService,
  ) {}

  /**
   * Generates a list of Point of Delivery (POD) options based on the provided configuration.
   * @param config The configuration for building POD options.
   * @returns An array of generated POD options.
   */
  generatePodOptions(config: PodOptionsBuilderConfig): any[] {
    const podOptions = config.pods.map((shipmentLocation) => this.getOptionPOD(shipmentLocation, config));
    if (config.jobsite && config.jobsite.isVariousLocation) {
      podOptions.unshift(this.getOptionJobsite(config));
    }
    return podOptions;
  }

  /**
   * Creates an option for a POD based on shipment location and configuration.
   * @param shipmentLocation The shipment location data.
   * @param config Configuration for the POD options.
   * @returns The POD option object.
   */
  private getOptionPOD(shipmentLocation: IShipmentLocation, config: PodOptionsBuilderConfig) {
    const isVariousLocation = shipmentLocation.isVariousLocation;
    const shipmentLocationStatusCode = shipmentLocation.shipmentLocationStatus.shipmentLocationStatusCode;
    const isActiveShipmentLocation = shipmentLocationStatusCode !== STATUS_POD_CODE.INACTIVE;
    const validExperience = config.experienceCode
      ? config.experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
        config.experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
      : true;
    const isJobsite = config.jobsite
      ? shipmentLocation.shipmentLocationId === config.jobsite.shipmentLocationId
      : false;

    const isPodSelected = config.forms
      ? this.getIsPodSelected(config.forms, shipmentLocation, config.formGroup, config.fieldConfig)
      : false;

    const canDelete =
      !isVariousLocation &&
      isActiveShipmentLocation &&
      config.isEnablePod &&
      !isPodSelected &&
      validExperience &&
      !isJobsite;

    return this.createOption(shipmentLocation, config, canDelete);
  }

  /**
   * Constructs the display name for a POD based on its description and configuration.
   * @param shipmentLocation Shipment location data containing description and code.
   * @param isEnablePod Flag indicating if POD is enabled.
   * @returns The formatted POD name.
   */
  private getNamePod({ shipmentLocationDesc, shipmentLocationCode }: IShipmentLocation, isEnablePod: boolean): string {
    return isEnablePod ? shipmentLocationDesc : shipmentLocationCode.replace(/0+/, '') + ' - ' + shipmentLocationDesc;
  }

  /**
   * Retrieves the address name by the given address ID.
   * @param addressId The ID of the address.
   * @returns The name of the address.
   */
  private getAddressName(addressId: number): string {
    return this.addressService.findAddressNameByAddressId(addressId);
  }

  /**
   * Determines if a POD is selected based on the provided forms and configuration.
   * @param forms Dictionary of order forms.
   * @param shipmentLocation The shipment location being checked.
   * @param formGroup The form group containing POD controls.
   * @param fieldConfig The configuration for the form field.
   * @returns True if the POD is selected, false otherwise.
   */
  private getIsPodSelected(
    forms: Dictionary<any>,
    shipmentLocation: IShipmentLocation,
    formGroup: UntypedFormGroup,
    fieldConfig: FieldSelectConfig,
  ): boolean {
    const pointOfDeliveriesSelected = Object.keys(forms)
      .filter((key) => !forms[key].orderConfirmation || !forms[key].orderConfirmation.success)
      .map((key) => forms[key].pointOfDelivery);
    const isPodSelected =
      pointOfDeliveriesSelected.includes(shipmentLocation.shipmentLocationId) ||
      formGroup.controls[fieldConfig.name].value === shipmentLocation.shipmentLocationId;
    return isPodSelected;
  }

  /**
   * Creates an option for a jobsite location.
   * @param config Configuration data for the jobsite.
   * @returns The jobsite option object.
   */
  private getOptionJobsite(config: PodOptionsBuilderConfig) {
    return this.createOption(config.jobsite, config, false, true);
  }

  /**
   * Creates an option object for a given shipment location or jobsite.
   * @param shipmentLocation The shipment location or jobsite data.
   * @param config Configuration for the options.
   * @param canDelete Flag indicating if the option can be deleted.
   * @param isJobsite Flag indicating if the option is for a jobsite.
   * @returns The formatted option object.
   */
  private createOption(shipmentLocation, config: PodOptionsBuilderConfig, canDelete: boolean, isJobsite = false) {
    let baseOption = {
      canDelete,
    };

    const isEnablePodVL3 = this.featureToggleService.isFeatureEnabled(Features.ENABLED_JOBSITE_DROPDOWN_REPEAT_ORDER);
    if (isEnablePodVL3) {
      baseOption['secondaryText'] = this.getAddressName(
        isJobsite ? config.jobsite.address.addressId : shipmentLocation.address.addressId,
      );
    }

    const showJobsiteFullAddress = this.featureToggleService.isFeatureEnabled(Features.NOF_JOBSITE_FULL_ADDRESS);
    if (showJobsiteFullAddress) {
      const addressDetails = getOptionWithAddress(shipmentLocation, showJobsiteFullAddress);
      baseOption = {
        ...baseOption,
        ...addressDetails,
      };
    }

    if (config.useAlternativeKeys) {
      return {
        ...baseOption,
        value: isJobsite ? config.jobsite.shipmentLocationId : shipmentLocation.shipmentLocationId,
        label: this.getNamePod(isJobsite ? config.jobsite : shipmentLocation, config.isEnablePod),
      };
    } else {
      return {
        ...baseOption,
        id: isJobsite ? config.jobsite.shipmentLocationId : shipmentLocation.shipmentLocationId,
        name: this.getNamePod(isJobsite ? config.jobsite : shipmentLocation, config.isEnablePod),
      };
    }
  }

  findAddressNameByAddressId(addresId: number): string {
    return this.getAddressName(addresId);
  }
}
